import { windowEventsManager } from '@folklore/hooks';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const useSafeLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : () => {};

interface UseScrollRestorationOptions {
    index: number;
    shouldUpdate?: boolean;
    shouldReset?: boolean;
    shouldRestore?: boolean;
}

// Remembers the window scroll of each timeline page and restores it when the page becomes
// the current one again.
export default function useScrollRestoration({
    index: currentIndex,
    shouldUpdate = false,
    shouldReset = false,
    shouldRestore = false,
}: UseScrollRestorationOptions) {
    const scrollRef = useRef<number[]>([]);
    const [waitForRestore, setWaitForRestore] = useState(false);

    const getScroll = (index: number): number | null => scrollRef.current[index] || null;

    const restoreScroll = (index: number) => {
        window.scrollTo(0, scrollRef.current[index] || 0);
    };

    if (!shouldUpdate && !waitForRestore) {
        setWaitForRestore(true);
    }

    useEffect(() => {
        if (!shouldUpdate || waitForRestore) {
            return;
        }

        function getMaxScroll() {
            return document.documentElement.scrollHeight - window.innerHeight;
        }

        let maxScroll = getMaxScroll();
        function handleScroll() {
            const newScroll = window.scrollY || window.pageYOffset;
            if (newScroll === 0) {
                maxScroll = getMaxScroll();
            }
            const currentScroll = scrollRef.current[currentIndex] || 0;
            if (maxScroll > 0 && currentScroll !== newScroll) {
                scrollRef.current[currentIndex] = newScroll;
            }
        }

        windowEventsManager.subscribe('scroll', handleScroll);
        const timeout = setTimeout(() => {
            handleScroll();
        }, 10);

        return () => {
            windowEventsManager.unsubscribe('scroll', handleScroll);
            clearTimeout(timeout);
        };
    }, [currentIndex, shouldUpdate, waitForRestore]);

    useSafeLayoutEffect(() => {
        if (shouldRestore) {
            restoreScroll(currentIndex);
        }
    }, [currentIndex, shouldRestore, restoreScroll]);

    useEffect(() => {
        let timeout = null;
        if (shouldReset) {
            window.scrollTo(0, 0);
        } else if (shouldRestore) {
            restoreScroll(currentIndex);
            timeout = setTimeout(() => {
                setWaitForRestore(false);
            }, 100);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [currentIndex, shouldReset, shouldRestore, restoreScroll]);

    return {
        getScroll,
    };
}
